import { Input } from '@/components/shared/Input/Input'
import PaymentMethodSelector from '@/components/shared/PaymentMethodSelector/PaymentMethodSelector'
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect'
import { MultiComboBox } from '@/components/shared/MultiComboBox/MultiComboBox'
import StatusSelector from '@/components/shared/StatusSelector/StatusSelector'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'
import { SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import DateRangePicker from '@/pages/Dashboard/Partials/DateRangePicker'
import { IntegrationsService } from '@/services/integrations.service'
import { SellsService } from '@/services/sells.service'
import useCurrencyStore from '@/store/useCurrencyStore'
import useModalStore from '@/store/useModalStore'
import { useSoldProductFilterOptions } from '@/hooks/useSoldProductFilterOptions'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { PAYMENT_STATUS, STATUSES_BY_DATE_AXIS, SalesDateAxis } from '@/hooks/useSales'
import { Label } from '@/components/shared/Label/Label'
import { fetchReportTemplateOptions } from '@/utils/checkoutTemplates'


const FilterSchema = z.object({
  products: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  offers: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  affiliates: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  coproducers: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  templates: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  payment_methods: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
  invoice_approved: z.boolean().optional(),
  invoice_not_approved: z.boolean().optional(),
  src: z.string().optional(),
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_content: z.string().optional(),
  utm_term: z.string().optional(),
})

type FilterFormData = z.infer<typeof FilterSchema>

/** Valores iniciais podem vir da URL (string "1") ou do form (boolean) */
type FilterValuesInput = Omit<
  FilterFormData,
  'invoice_approved' | 'invoice_not_approved'
> & {
  invoice_approved?: boolean | string
  invoice_not_approved?: boolean | string
}

export default function FilterSales({
  onFilter,
  filterValues,
  dateAxis,
  dateRange,
  onDateAxisChange,
  onDateRangeChange,
}: {
  filterValues: FilterValuesInput
  onFilter: (data: FilterFormData) => void
  dateAxis: SalesDateAxis
  dateRange: DateRange | undefined
  onDateAxisChange: (axis: SalesDateAxis) => void
  onDateRangeChange: (range: DateRange | undefined) => void
}) {
  const { t } = useTranslation(['sales', 'common', 'reports'])
  const { closeModal } = useModalStore()
  const { currency } = useCurrencyStore()

  const { data: soldPaymentMethods = [] } = useQuery({
    queryKey: ['sold-payment-methods', currency],
    queryFn: () => SellsService.getSoldPaymentMethods(currency),
    staleTime: 60_000,
  })

  const { data: notazzIntegrations } = useQuery({
    queryKey: ['integrations', 'notazz'],
    queryFn: () =>
      IntegrationsService.getIntegration({ integration: 'notazz', page: 1 }),
    staleTime: 60_000,
  })
  const hasNotazz = (notazzIntegrations?.data?.length ?? 0) > 0

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    reset,
    getValues,
  } = useForm<FilterFormData>({
    shouldUnregister: false,
    resolver: zodResolver(FilterSchema),
    defaultValues: {
      products: filterValues?.products || [],
      offers: filterValues?.offers || [],
      affiliates: filterValues?.affiliates || [],
      coproducers: filterValues?.coproducers || [],
      templates: filterValues?.templates || [],
      payment_methods: filterValues?.payment_methods || [],
      status: filterValues?.status?.length ? filterValues.status : Array.from(PAYMENT_STATUS),
      invoice_approved:
        filterValues?.invoice_approved === '1' ||
        filterValues?.invoice_approved === 'true' ||
        filterValues?.invoice_approved === true ||
        false,
      invoice_not_approved:
        filterValues?.invoice_not_approved === '1' ||
        filterValues?.invoice_not_approved === 'true' ||
        filterValues?.invoice_not_approved === true ||
        false,
      src: filterValues?.src || undefined,
      utm_source: filterValues?.utm_source || undefined,
      utm_medium: filterValues?.utm_medium || undefined,
      utm_campaign: filterValues?.utm_campaign || undefined,
      utm_content: filterValues?.utm_content || undefined,
      utm_term: filterValues?.utm_term || undefined,
    },
  })

  useEffect(() => {
    const incoming = filterValues?.status?.length
      ? (filterValues.status as string[])
      : [];
    const current = watch('status') ?? [];
    const same =
      incoming.length === current.length &&
      incoming.every((s) => current.includes(s));
    if (!same) {
      setValue('status', incoming);
    }
  }, [filterValues?.status]);

  useEffect(() => {
    if (filterValues?.payment_methods?.length) return;
    if (soldPaymentMethods.length === 0) return;
    const types = soldPaymentMethods.map(m => m.type);
    const current = watch('payment_methods') ?? [];
    const same =
      current.length === types.length &&
      types.every(t => current.includes(t));
    if (!same) {
      setValue('payment_methods', types);
    }
  }, [soldPaymentMethods, filterValues?.payment_methods]);

  const {
    productIds,
    productIdsKey,
    fetchProductOptions,
    fetchOfferOptions,
    fetchAffiliatesOptions,
    fetchCoProducersOptions,
    handleProductsChange,
    handleOffersChange,
    queryKeys,
  } = useSoldProductFilterOptions({
    watch,
    setValue,
    getValues,
    currency,
    scope: 'sales',
    enableAffiliates: true,
    enableCoproducers: true,
  });

  const defaultValues = watch()

  const onSubmit = async (data: any) => {
    onFilter(data)
    closeModal('filter-sales')
  }

  const arrayFilterKeys = [
    'products',
    'offers',
    'affiliates',
    'coproducers',
    'payment_methods',
    'status',
  ] as const

  const handleSelectAll = <T extends typeof arrayFilterKeys[number]>(
    field: T,
    items: FilterFormData[T],
  ) => {
    const currentValues = defaultValues[field]
    if (!Array.isArray(currentValues) || !Array.isArray(items)) return
    const isSelectedAll = currentValues.length === items.length
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue(field, (isSelectedAll ? [] : items) as any)
  }

  return (
    <section className="h-full flex relative flex-col justify-between">
      <SheetHeader className="md:p-6 p-4">
        <SheetTitle className="flex gap-2">{t('filters')}</SheetTitle>
      </SheetHeader>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <section className="flex  relative flex-col gap-4 px-4  md:px-6">
          <div className="space-y-4">
            <CustomSelect
              label={t('date_type_label')}
              placeholder={t('date_type_label')}
              value={dateAxis}
              onValueChange={(value) =>
                onDateAxisChange(value as SalesDateAxis)
              }
              options={[
                { value: 'processing', label: t('date_type_processing') },
                { value: 'paid', label: t('date_type_paid') },
                { value: 'refunded', label: t('date_type_cancelled') },
              ]}
            />

            <div className="space-y-2">
              <Label>{t('date_range_label')}</Label>
              <DateRangePicker
                defaultValue={dateRange}
                onChange={onDateRangeChange}
                widthFull
              />
            </div>
          </div>

          <MultiComboBox
            label={t('products')}
            placeholder={t('products_placeholder')}
            fetchOptions={fetchProductOptions}
            values={watch('products') ?? []}
            onChange={(value) => {
              handleProductsChange(value)
              clearErrors('products')
            }}
            helperText={errors.products?.message}
            queryKey="sold-products"
            returnFullObject
            cypressName="multicombobox-products"
            selectAllOptions
            newParams={[currency]}
          />

          <MultiComboBox
            key={`offers-${productIdsKey}`}
            label={t('offer')}
            placeholder={t('offer_placeholder')}
            fetchOptions={fetchOfferOptions}
            values={watch('offers') ?? []}
            onChange={(values) => {
              handleOffersChange(values)
              clearErrors('offers')
            }}
            helperText={errors.offers?.message}
            queryKey={queryKeys.offers}
            returnFullObject
            cypressName="multicombobox-offers"
            selectAllOptions
            newParams={productIds}
            disabled={productIds.length === 0}
          />

          <MultiComboBox
            key={`affiliates-${productIdsKey}`}
            label={t('affiliate')}
            placeholder={t('affiliate_placeholder')}
            fetchOptions={fetchAffiliatesOptions}
            values={watch('affiliates') ?? []}
            onChange={(value) => {
              setValue('affiliates', value)
              clearErrors('affiliates')
            }}
            helperText={errors.affiliates?.message}
            queryKey={queryKeys.affiliates}
            returnFullObject
            cypressName="multicombobox-affiliates"
            selectAllOptions
            newParams={productIds}
          />

          <MultiComboBox
            key={`coproducers-${productIdsKey}`}
            label={t('co_producer')}
            placeholder={t('co_producer_placeholder')}
            fetchOptions={fetchCoProducersOptions}
            values={watch('coproducers') ?? []}
            onChange={(value) => {
              setValue('coproducers', value)
              clearErrors('coproducers')
            }}
            helperText={errors.coproducers?.message}
            queryKey={queryKeys.coproducers}
            returnFullObject
            cypressName="multicombobox-coproducers"
            selectAllOptions
            newParams={productIds}
          />

          <MultiComboBox
            label={t('reports:template')}
            placeholder={t('reports:select_template')}
            fetchOptions={fetchReportTemplateOptions}
            values={watch('templates') ?? []}
            onChange={(value) => {
              setValue('templates', value)
              clearErrors('templates')
            }}
            helperText={errors.templates?.message}
            queryKey="report-templates-sales"
            returnFullObject
            cypressName="multicombobox-templates"
            selectAllOptions
          />

          <Separator className="my-4" />

          <PaymentMethodSelector
            variant="compact"
            availableMethods={soldPaymentMethods}
            selectedMethods={defaultValues.payment_methods ?? []}
            onChange={(methods) => {
              setValue('payment_methods', methods)
              clearErrors('payment_methods')
            }}
            emptyMessage={t('common:payment_methods_no_history')}
          />

          {hasNotazz && (
            <>
              <Separator className="my-4" />
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-lg font-semibold">
                    {t('invoiceStatus')}
                  </h2>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="flex gap-2">
                    <Checkbox
                      onCheckedChange={(checked) =>
                        setValue('invoice_approved', !!checked)
                      }
                      checked={defaultValues.invoice_approved === true}
                      id="invoice_approved"
                      data-cy="checkbox-invoice-approved"
                      name="invoice_approved"
                    />
                    <label
                      htmlFor="invoice_approved"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t('filterInvoiceApproved')}
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <Checkbox
                      onCheckedChange={(checked) =>
                        setValue('invoice_not_approved', !!checked)
                      }
                      checked={defaultValues.invoice_not_approved === true}
                      id="invoice_not_approved"
                      data-cy="checkbox-invoice-not-approved"
                      name="invoice_not_approved"
                    />
                    <label
                      htmlFor="invoice_not_approved"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t('filterInvoiceNotApproved')}
                    </label>
                  </div>
                </div>
              </div>
              <Separator className="my-4" />
            </>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">{t('payment_status')}</h2>
              <span
                className="text-sm font-medium text-primary hover:text-primary/80 cursor-pointer"
                onClick={() =>
                  handleSelectAll('status', Array.from(STATUSES_BY_DATE_AXIS[dateAxis] ?? PAYMENT_STATUS))
                }
              >
                {t('select_all')}
              </span>
            </div>
            <StatusSelector
              options={(STATUSES_BY_DATE_AXIS[dateAxis] ?? PAYMENT_STATUS).map(status => ({ value: status, label: t(`common:${status}`) }))}
              selectedOptions={defaultValues.status ?? []}
              onChange={(statuses) => {
                setValue('status', statuses);
                clearErrors('status');
              }}
              variant="compact"
            />
          </div >

          <Separator className="my-4" />

          <div className="space-y-4 mb-24">
            <h2 className="text-lg font-semibold">{t('url_parameters')}</h2>
            <Input
              {...register('src')}
              label={t('src')}
              placeholder={t('src_placeholder')}
              helperText={errors.src?.message}
              data-cy="input-src"
            />
            <Input
              {...register('utm_source')}
              label={t('utm_source')}
              placeholder={t('utm_source_placeholder')}
              helperText={errors.utm_source?.message}
              data-cy="input-utm-source"
            />
            <Input
              {...register('utm_medium')}
              label={t('utm_medium')}
              placeholder={t('utm_medium_placeholder')}
              helperText={errors.utm_medium?.message}
              data-cy="input-utm-medium"
            />
            <Input
              {...register('utm_campaign')}
              label={t('utm_campaign')}
              placeholder={t('utm_campaign_placeholder')}
              helperText={errors.utm_campaign?.message}
              data-cy="input-utm-campaign"
            />
            <Input
              {...register('utm_content')}
              label={t('utm_content')}
              placeholder={t('utm_content_placeholder')}
              helperText={errors.utm_content?.message}
              data-cy="input-utm-content"
            />
            <Input
              {...register('utm_term')}
              label={t('utm_term')}
              placeholder={t('utm_term_placeholder')}
              helperText={errors.utm_term?.message}
              data-cy="input-utm-term"
            />
          </div>
        </section >

        <SheetFooter className="flex z-50 sticky bottom-0 bg-[#09090b] w-full border-t border-t-colorBorder p-4 items-end mt-4 justify-end">
          <div className="flex w-full z-50 gap-2 justify-end items-end">
            <Button
              variant="ghost"
              className="w-full !text-colorText md:max-w-[118px]"
              onClick={() => closeModal('filter-sales')}
              type="button"
            >
              {t('cancel')}
            </Button>
            <Button
              variant="default"
              className="w-full md:max-w-fit"
              type="submit"
              data-cy="apply-filters"
            >
              {t('apply_filters')}
            </Button>
          </div>
        </SheetFooter>
      </form >
    </section >
  )
}
